import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Check, CreditCard } from "lucide-react";
import { startStripeCheckout } from "@/lib/payments";
import { useAuth } from "@/context/AuthContext";
import { MemberSignInHint } from "@/components/MemberSignInHint";

const GUMROAD_BUNDLE = "https://richculm.gumroad.com/l/lkejj";

const options = [
  {
    key: "workbook",
    name: "The Audio",
    price: "$37",
    tagline: "The narrated audio journey",
    features: [
      "The full narrated audio — Rich guiding you",
      "All 8 modules to listen to anywhere",
      "Self-paced, lifetime access",
    ],
    external: "https://www.amazon.com/dp/B0H5D49156",
    externalLabel: "Also on Amazon",
  },
  {
    key: "course",
    name: "The Video Course",
    price: "$57",
    tagline: "All 8 teaching videos",
    featured: true,
    features: [
      "All 8 teaching videos with Rich",
      "Guidance through every principle & article",
      "Lifetime access",
    ],
    external: GUMROAD_BUNDLE,
    externalLabel: "Full videos of Rich on Gumroad",
  },
  {
    key: "bundle",
    name: "The Complete Bundle",
    price: "$77",
    tagline: "Audio + all 8 videos",
    features: [
      "The Audio AND the Video Course",
      "The best way to learn and do the work",
      "Best value · save vs. buying separately",
      "Community access",
    ],
    external: GUMROAD_BUNDLE,
    externalLabel: "Full videos of Rich on Gumroad",
  },
];

export default function PricingBlock() {
  const { user } = useAuth();
  const userId = user && user.id ? user.id : null;

  useEffect(() => {
    if (window.location.hash !== "#pricing") return;
    const t = setTimeout(() => {
      const el = document.getElementById("pricing");
      if (el) window.scrollTo({ top: el.getBoundingClientRect().top + window.pageYOffset - 96, behavior: "smooth" });
    }, 400);
    return () => clearTimeout(t);
  }, []);

  return (
    <section id="pricing" className="relative bg-[#FFFFFF] py-28 md:py-36 border-y border-[#E7DFD5]">
      <div className="max-w-[1440px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-12 gap-y-10 mb-16">
          <div className="col-span-12 md:col-span-6">
            <div className="section-tag mb-6">§ Begin Your Healing Journey</div>
            <h2 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.9] tracking-tight">
              One journey. Three <span className="italic text-[#B45309]">ways in</span>.
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8 flex items-end">
            <p className="text-[#44403C] text-lg leading-relaxed max-w-md">
              Choose how you want to begin. Buy securely with card right here for the best price and instant, lifetime access — and every dollar goes straight to Rich. Prefer the full-length videos of Rich teaching on camera? Those live on Gumroad, so the choice is yours.
            </p>
          </div>
        </div>

        <MemberSignInHint className="mb-8" />

        <div className="grid grid-cols-12 border border-[#E7DFD5]">
          {options.map((o, i) => (
            <motion.div
              key={o.key}
              data-testid={`pricing-option-${o.key}`}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: i * 0.08, ease: [0.65, 0, 0.35, 1] }}
              className={`col-span-12 md:col-span-4 p-8 md:p-12 relative flex flex-col ${i < options.length - 1 ? "md:border-r border-[#E7DFD5]" : ""} ${i > 0 ? "border-t md:border-t-0 border-[#E7DFD5]" : ""} ${o.featured ? "bg-[#F5EFEB]" : ""}`}
            >
              {o.featured && <div className="absolute top-6 right-6 section-tag text-[#B45309]">Most popular</div>}
              <div className="font-mono text-[10px] tracking-[0.3em] uppercase text-[#78716C] mb-3">{o.tagline}</div>
              <div className="font-display text-4xl md:text-5xl mb-5">{o.name}</div>
              <div className="font-display text-6xl md:text-7xl text-[#B45309] mb-8">{o.price}</div>
              <ul className="space-y-3 mb-10 flex-1">
                {o.features.map((f) => (
                  <li key={f} className="flex items-start gap-3 text-sm text-[#44403C]">
                    <Check size={16} className="text-[#B45309] mt-1 shrink-0" strokeWidth={1.5} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => startStripeCheckout(o.key, userId)}
                  data-testid={`pricing-buy-${o.key}`}
                  className={`${o.featured ? "btn-ember" : "btn-outline"} text-sm w-full justify-center`}
                >
                  <CreditCard size={16} /> Buy with Card — {o.price}
                </button>
                <a
                  href={o.external}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`pricing-external-${o.key}`}
                  className="text-center font-mono text-[10px] tracking-[0.25em] uppercase text-[#78716C] hover:text-[#B45309] transition-colors"
                >
                  {o.externalLabel} ↗
                </a>
              </div>
            </motion.div>
          ))}
        </div>
        <p className="font-mono text-[9px] tracking-[0.2em] uppercase text-[#A8A29E] mt-8 max-w-2xl" data-testid="checkout-terms-note">
          Card payments are processed by Stripe. By buying you agree to the <Link to="/terms" className="text-[#78716C] underline underline-offset-2">Terms</Link> and <Link to="/privacy" className="text-[#78716C] underline underline-offset-2">Privacy Policy</Link>. Sold by A.BC.D.F.LLC · 14-day make-it-right promise.
        </p>
      </div>
    </section>
  );
}
